import {
  AddressInformationResponse,
  ContactInformationResponse,
  ContractInformationRequest,
  ExportFormatEnumDto,
  RoleEnumDto,
  StackNumberingEnumDto,
  UserModelDto,
} from '../.generated/api';

import { AxiosError } from 'axios';
import { notificationActions } from 'actions/notificationActions';

import { ApiService, OrganizationListQuery } from 'services/apiService';
import { ApplicationState, AppDispatch } from 'store';
import { OrganizationListFilters } from 'store/organizations';

const defaultOrganizationFilters: OrganizationListFilters = {
  page: 1,
  pageSize: 25,
};

const normalizeFilters = (
  filters?: Partial<OrganizationListFilters>,
  currentFilters?: OrganizationListFilters,
): OrganizationListFilters => {
  const merged = {
    ...(currentFilters ?? defaultOrganizationFilters),
    ...(filters ?? {}),
  };

  return {
    page: merged.page > 0 ? merged.page : 1,
    pageSize:
      merged.pageSize > 0
        ? merged.pageSize
        : defaultOrganizationFilters.pageSize,
    name: merged.name?.trim() || undefined,
    active: merged.active,
    contractStatus: merged.contractStatus,
  };
};

function getOrganizations(filters?: Partial<OrganizationListFilters>) {
  return (dispatch: AppDispatch, getState: () => ApplicationState) => {
    const state = getState();
    const currentFilters = state.organizations?.filters;
    const normalizedFilters = normalizeFilters(filters, currentFilters);
    const query: OrganizationListQuery = {
      page: normalizedFilters.page,
      pageSize: normalizedFilters.pageSize,
      name: normalizedFilters.name,
      active: normalizedFilters.active,
      contractStatus: normalizedFilters.contractStatus,
    };

    dispatch({
      type: 'ORGANIZATION_LIST_SUCCESS',
      organizations: undefined,
      filters: normalizedFilters,
    });
    ApiService.getOrganizationsDetailed(query)
      .then((response) => {
        dispatch({
          type: 'ORGANIZATION_LIST_SUCCESS',
          organizations: response?.data ?? null,
          filters: normalizedFilters,
        });
      })
      .catch((error) => {
        dispatch({
          type: 'ORGANIZATION_LIST_SUCCESS',
          organizations: null,
          filters: normalizedFilters,
        });
        dispatch(notificationActions.enqueueErrorResponse(error));
      });
  };
}

function clearOrganization() {
  return { type: 'GET_ORGANIZATION_SUCCESS', organization: undefined };
}

function getOrganizationById(organizationId: string) {
  return (dispatch: AppDispatch) => {
    return ApiService.getOrganizationById(organizationId)
      .then((response) => {
        dispatch({
          type: 'GET_ORGANIZATION_SUCCESS',
          organization: response.data,
        });
      })
      .catch((error) => {
        dispatch({
          type: 'GET_ORGANIZATION_ERROR',
          error: error.response?.data?.errorMessage,
        });
        dispatch(notificationActions.enqueueErrorResponse(error));
      });
  };
}

function createOrganization(name: string, country: string, groupCode: string) {
  return (dispatch: AppDispatch) => {
    dispatch({ type: 'SET_IS_PROCESSING', isProcessing: true });

    ApiService.createOrganization(name, country, groupCode)
      .then((response) => {
        dispatch({ type: 'SET_IS_PROCESSING', isProcessing: false });
        dispatch({
          type: 'CREATE_ORGANIZATION_SUCCESS',
          organization: response.data,
        });
        dispatch({ type: 'SET_STEP', step: 2 });
        dispatch(organizationActions.getOrganizations());
      })
      .catch((error) => {
        // TODO MAL: use error code once that exists
        dispatch({ type: 'SET_IS_PROCESSING', isProcessing: false });
        dispatch({
          type: 'UPDATE_ORGANIZATION_ERROR',
          error: error.response?.data?.errorMessage,
        });
      });
  };
}

function updateCurrentContract(
  organizationId: string,
  currentContract: ContractInformationRequest,
  callback?: (error?: AxiosError) => void,
) {
  return (dispatch: AppDispatch) => {
    ApiService.updateContractInformation(organizationId, currentContract)
      .then(() => {
        dispatch({
          type: 'UPDATE_CURRENT_CONTRACT',
          currentContract,
        });
        callback && callback();
      })
      .catch((error) => {
        if (callback) {
          callback(error);
        } else {
          dispatch(notificationActions.enqueueErrorResponse(error));
        }
      });
  };
}

function getCurrentContract(organizationId: string) {
  return (dispatch: AppDispatch) => {
    dispatch({
      type: 'UPDATE_CURRENT_CONTRACT',
      currentContract: undefined,
    });
    ApiService.getCurrentContract(organizationId)
      .then((response) => {
        dispatch({
          type: 'UPDATE_CURRENT_CONTRACT',
          currentContract: response.data,
        });
      })
      .catch((error) => {
        dispatch({
          type: 'UPDATE_CURRENT_CONTRACT',
          currentContract: null,
        });
        if (error.response?.status !== 404) {
          // 404 means no contract available
          dispatch(notificationActions.enqueueErrorResponse(error));
        }
      });
  };
}

function getNextContract(organizationId: string) {
  return (dispatch: AppDispatch) => {
    dispatch({
      type: 'UPDATE_NEXT_CONTRACT',
      nextContract: undefined,
    });
    ApiService.getNextContract(organizationId)
      .then((response) => {
        dispatch({
          type: 'UPDATE_NEXT_CONTRACT',
          nextContract: response.data,
        });
      })
      .catch((error) => {
        dispatch({
          type: 'UPDATE_NEXT_CONTRACT',
          nextContract: null,
        });
        if (error.response?.status !== 404) {
          // 404 means no contract available
          dispatch(notificationActions.enqueueErrorResponse(error));
        }
      });
  };
}

function updateNextContract(
  organizationId: string,
  contract: ContractInformationRequest,
  callback?: (error?: AxiosError) => void,
) {
  return (dispatch: AppDispatch) => {
    ApiService.updateNextContract(organizationId, contract)
      .then(() => {
        dispatch({
          type: 'UPDATE_NEXT_CONTRACT',
          nextContract: contract,
        });
        callback && callback();
      })
      .catch((error) => {
        if (callback) {
          callback(error);
        } else {
          dispatch(notificationActions.enqueueErrorResponse(error));
        }
      });
  };
}

function getOrganizationLogo(organizationId: string) {
  return (dispatch: AppDispatch) => {
    ApiService.getOrganizationLogo(organizationId)
      .then((response) => {
        dispatch({
          type: 'UPDATE_ORGANIZATION_LOGO',
          logo: response?.data?.image,
        });
      })
      .catch((error: AxiosError) => {
        // clear logo
        dispatch({
          type: 'UPDATE_ORGANIZATION_LOGO',
          logo: '',
        });
        if (error.response?.status !== 404) {
          // 404 means no logo available
          dispatch(notificationActions.enqueueErrorResponse(error));
        }
      });
  };
}

function updateOrganizationLogo(organizationId: string, file: string | Blob) {
  return (dispatch: AppDispatch, getState: () => ApplicationState) => {
    const state = getState();
    const oldLogo = state.organization?.logo;
    dispatch({
      type: 'UPDATE_ORGANIZATION_LOGO',
      logo: undefined,
    });
    ApiService.updateOrganizationLogo(organizationId, file)
      .then(() => {
        dispatch(getOrganizationLogo(organizationId));
      })
      .catch((error) => {
        dispatch({
          type: 'UPDATE_ORGANIZATION_LOGO',
          logo: oldLogo,
        });
        dispatch(notificationActions.enqueueErrorResponse(error));
      });
  };
}

function addContractualInfo(
  organizationId: string,
  contractinformation: ContractInformationRequest,
) {
  return (dispatch: AppDispatch) => {
    dispatch({ type: 'SET_IS_PROCESSING', isProcessing: true });

    ApiService.updateContractInformation(organizationId, contractinformation)
      .then(() => {
        dispatch({ type: 'SET_IS_PROCESSING', isProcessing: false });
        dispatch({ type: 'SET_STEP', step: 3 });
        dispatch(organizationActions.getOrganizations());
      })
      .catch((error) => {
        // TODO MAL: use error code once that exists
        dispatch({ type: 'SET_IS_PROCESSING', isProcessing: false });
        dispatch({
          type: 'CREATE_ORGANIZATION_FAILURE',
          error: error.response?.data?.errorMessage,
        });
      });
  };
}

function addAdminToOrg(
  organizationId: string,
  email: string,
  name: string,
  phone: string,
) {
  return (dispatch: AppDispatch) => {
    dispatch({ type: 'SET_IS_PROCESSING', isProcessing: true });

    ApiService.addOrganizationAdmin(organizationId, email, name, phone)
      .then(() => {
        dispatch(organizationActions.getOrganizations());
        dispatch({ type: 'SET_IS_PROCESSING', isProcessing: false });
        dispatch({ type: 'SET_STEP', step: 4 });
      })
      .catch((error) => {
        // TODO MAL: use error code once that exists
        dispatch({ type: 'SET_IS_PROCESSING', isProcessing: false });
        dispatch({
          type: 'CREATE_ORGANIZATION_FAILURE',
          error: error.response?.data?.errorMessage,
        });
      });
  };
}

function editMeasurementCard(
  organizationId: string,
  measurementCardId: string,
  stackNumbering: StackNumberingEnumDto,
  exportFormat: ExportFormatEnumDto = ExportFormatEnumDto.CSV,
  callback?: (error?: AxiosError) => void,
) {
  return (dispatch: AppDispatch) => {
    dispatch({ type: 'SET_IS_PROCESSING', isProcessing: true });

    ApiService.updateMeasurementCardInformation(
      organizationId,
      measurementCardId,
      stackNumbering,
      exportFormat,
    )
      .then(() => {
        dispatch(organizationActions.getOrganizations());
        dispatch(organizationActions.getOrganizationById(organizationId));
        dispatch({
          type: 'UPDATE_ORGANIZATION_SUCCESS',
        });
        if (callback) {
          callback();
        }
      })
      .catch((error) => {
        dispatch({
          type: 'UPDATE_ORGANIZATION_ERROR',
          error: error.response?.data?.errorMessage,
        });
        if (callback) {
          callback(error);
        }
      });
  };
}

function assignMeasurementCardToOrg(
  organizationId: string,
  measurementCardId: string,
  stackNumbering: StackNumberingEnumDto,
  exportFormat: ExportFormatEnumDto = ExportFormatEnumDto.CSV,
) {
  return (dispatch: AppDispatch) => {
    dispatch({ type: 'SET_IS_PROCESSING', isProcessing: true });

    ApiService.updateMeasurementCardInformation(
      organizationId,
      measurementCardId,
      stackNumbering,
      exportFormat,
    )
      .then(() => {
        dispatch(organizationActions.getOrganizations());

        dispatch({ type: 'SET_IS_PROCESSING', isProcessing: false });
        dispatch({ type: 'SET_FLOW_COMPLETED' });
      })
      .catch((error) => {
        // TODO MAL: use error code once that exists
        dispatch({ type: 'SET_IS_PROCESSING', isProcessing: false });
        dispatch({
          type: 'CREATE_ORGANIZATION_FAILURE',
          error: error.response?.data?.errorMessage,
        });
      });
  };
}

function createDept(
  organizationId: string,
  departmentName: string,
  departmentNumber?: number,
) {
  return (dispatch: AppDispatch) => {
    dispatch({ type: 'SET_IS_PROCESSING', isProcessing: true });

    ApiService.createDeptment(organizationId, departmentName, departmentNumber)
      .then((response) => {
        dispatch({ type: 'SET_IS_PROCESSING', isProcessing: false });
        dispatch({
          type: 'CREATE_DEPARTMENT_SUCCESS',
          department: response.data,
        });
        dispatch({ type: 'SET_STEP', step: 2 });
        dispatch(organizationActions.getOrganizationById(organizationId));
      })
      .catch((error) => {
        // TODO MAL: use error code once that exists
        dispatch({ type: 'SET_IS_PROCESSING', isProcessing: false });
        dispatch({
          type: 'CREATE_DEPARTMENT_FAILURE',
          error: error.response?.data?.errorMessage,
        });
        dispatch(notificationActions.enqueueErrorResponse(error));
      });
  };
}

function addUserToOrganization(
  organizationId: string,
  user: UserModelDto,
  callback: (error?: string) => void,
) {
  return (dispatch: AppDispatch) => {
    ApiService.addUserToOrganization(organizationId, user)
      .then(() => {
        dispatch(organizationActions.getOrganizationById(organizationId));
        callback();
      })
      .catch((error) => {
        // TODO MAL: handle error in a more general way instead of using a callback
        callback(error.response?.data?.errorMessage ?? error.message);
      });
  };
}

function editUserInOrganization(
  organizationId: string,
  email: string,
  name: string,
  phone: string,
  roles: RoleEnumDto[],
  enableAutoEmails: boolean,
  callback: (error?: string) => void,
) {
  return (dispatch: AppDispatch) => {
    ApiService.editUserInOrganization(
      organizationId,
      email,
      name,
      phone,
      roles,
      enableAutoEmails,
    )
      .then(() => {
        dispatch(organizationActions.getOrganizationById(organizationId));
        callback();
      })
      .catch((error) => {
        // TODO MAL: handle error in a more general way instead of using a callback
        callback(error.response?.data?.errorMessage ?? error.message);
      });
  };
}

function addContact(
  organizationId: string,
  name: string | undefined,
  email: string | undefined,
  phone: string | undefined,
  callback: (error?: string) => void,
) {
  return (dispatch: AppDispatch) => {
    ApiService.addOrganizationContact(organizationId, name, email, phone)
      .then(() => {
        dispatch(organizationActions.getOrganizationById(organizationId));
        callback();
      })
      .catch((error) => {
        // TODO MAL: handle error in a more general way instead of using a callback
        callback(error.response?.data?.errorMessage ?? error.message);
      });
  };
}

function editContact(
  organizationId: string,
  contactId: string,
  name: string | undefined,
  email: string | undefined,
  phone: string | undefined,
  callback: (error?: string) => void,
) {
  return (dispatch: AppDispatch) => {
    ApiService.updateOrganizationContact(
      organizationId,
      contactId,
      name,
      email,
      phone,
    )
      .then(() => {
        dispatch(organizationActions.getOrganizationById(organizationId));
        callback();
      })
      .catch((error) => {
        // TODO MAL: handle error in a more general way instead of using a callback
        callback(error.response?.data?.errorMessage ?? error.message);
      });
  };
}

function deleteContact(
  organizationId: string,
  contactId: string,
  callback: (error?: AxiosError) => void,
) {
  return (dispatch: AppDispatch) => {
    ApiService.deleteOrganizationContact(organizationId, contactId)
      .then(() => {
        dispatch(organizationActions.getOrganizationById(organizationId));
        callback();
      })
      .catch((error) => {
        callback(error);
      });
  };
}

function updateOrganization(
  organizationId: string,
  organizationName: string,
  groupCode: string,
  preferLinksInEmail: boolean,
  callback?: (error?: AxiosError) => void,
) {
  return (dispatch: AppDispatch) => {
    ApiService.updateOrganization(
      organizationId,
      organizationName,
      groupCode,
      preferLinksInEmail,
    )
      .then(() => {
        const refresh = dispatch(
          organizationActions.getOrganizationById(organizationId),
        );
        return Promise.resolve(refresh).then(() => callback?.());
      })
      .catch((error) => {
        if (callback) {
          callback(error);
        } else {
          dispatch(notificationActions.enqueueErrorResponse(error));
        }
      });
  };
}

function updateOrganizationAddressDetails(
  organizationId: string,
  organizationAddress: AddressInformationResponse,
  callback?: (error?: AxiosError) => void,
) {
  return (dispatch: AppDispatch) => {
    dispatch({
      type: 'ORGANIZATION_REMOVE_ERROR',
    });
    ApiService.updateOrganizationAddressDetails(
      organizationId,
      organizationAddress,
    )
      .then(() => {
        dispatch({
          type: 'ORGANIZATION_REMOVE_ERROR',
        });

        dispatch(organizationActions.getOrganizationById(organizationId));

        dispatch({
          type: 'UPDATE_ORGANIZATION_SUCCESS',
        });

        if (callback) {
          callback();
        }
      })
      .catch((error) => {
        dispatch({
          type: 'UPDATE_ORGANIZATION_ERROR',
          error: error.response?.data?.errorMessage,
        });
        if (callback) {
          callback(error);
        } else {
          dispatch(notificationActions.enqueueErrorResponse(error));
        }
      });
  };
}

function updateOrganizationContactDetails(
  organizationId: string,
  organizationContactInformation: ContactInformationResponse,
  callback?: (error?: AxiosError) => void,
) {
  return (dispatch: AppDispatch) => {
    dispatch({
      type: 'ORGANIZATION_REMOVE_ERROR',
    });

    ApiService.updateOrganizationContactDetails(
      organizationId,
      organizationContactInformation,
    )
      .then(() => {
        dispatch(organizationActions.getOrganizationById(organizationId));
        dispatch({
          type: 'UPDATE_ORGANIZATION_SUCCESS',
        });
        if (callback) {
          callback();
        }
      })
      .catch((error) => {
        dispatch({
          type: 'UPDATE_ORGANIZATION_ERROR',
          error: error.response?.data?.errorMessage,
        });
        if (callback) {
          callback(error);
        }
      });
  };
}

function prepareForest(
  organizationId: string,
  regionCode: number,
  inspectorateCode: number,
  callback?: (error?: AxiosError) => void,
) {
  return (dispatch: AppDispatch) => {
    ApiService.prepareForest(organizationId, regionCode, inspectorateCode)
      .then(() => {
        dispatch(organizationActions.getOrganizationById(organizationId));
        if (callback) {
          callback();
        }
      })
      .catch((error) => {
        if (callback) {
          callback(error);
        } else {
          dispatch(notificationActions.enqueueErrorResponse(error));
        }
      });
  };
}

export const organizationActions = {
  createOrganization,
  clearOrganization,
  getOrganizationById,
  addAdminToOrg,
  addContractualInfo,
  updateCurrentContract,
  getCurrentContract,
  getNextContract,
  updateNextContract,
  getOrganizations,
  editMeasurementCard,
  assignMeasurementCardToOrg,
  createDept,
  addContact,
  editContact,
  deleteContact,
  updateOrganization,
  updateOrganizationContactDetails,
  updateOrganizationAddressDetails,
  getOrganizationLogo,
  updateOrganizationLogo,
  addUserToOrganization,
  editUserInOrganization,
  prepareForest,
};
